import axios from "axios";
import { MAIN_URL } from "../Configurations/Urls";

const admin = `${MAIN_URL}/api/admin`;
const wallets = `${MAIN_URL}/api/wallets`;

function unwrap(res) {
  return res.data?.data ?? null;
}

export async function fetchExternalApplications() {
  return unwrap(await axios.get(`${admin}/external-applications`)) ?? [];
}

export async function fetchClients() {
  return unwrap(await axios.get(`${admin}/clients`)) ?? [];
}

export async function fetchClient(clientId) {
  return unwrap(await axios.get(`${admin}/clients/${clientId}`));
}

export async function updateClient(clientId, payload) {
  return unwrap(await axios.put(`${admin}/clients/${clientId}`, payload));
}

export async function setClientApiSecret(clientId, secret) {
  return unwrap(
    await axios.put(
      `${admin}/clients/${clientId}/api-secret`,
      secret ? { secret } : {}
    )
  );
}

export async function fetchClientAllowedIps(clientId) {
  return unwrap(await axios.get(`${admin}/clients/${clientId}/allowed-ips`)) ?? [];
}

export async function addClientAllowedIp(clientId, ipAddress, label) {
  return unwrap(
    await axios.post(`${admin}/clients/${clientId}/allowed-ips`, {
      ip_address: ipAddress,
      label: label || undefined,
    })
  );
}

export async function deleteClientAllowedIp(clientId, id) {
  await axios.delete(`${admin}/clients/${clientId}/allowed-ips/${id}`);
}

export async function updateExternalApplication(externalApplicationId, payload) {
  return unwrap(
    await axios.put(
      `${admin}/external-applications/${externalApplicationId}`,
      payload
    )
  );
}

export async function setTransactionPassword(externalApplicationId, password) {
  return unwrap(
    await axios.put(
      `${admin}/external-applications/${externalApplicationId}/transaction-password`,
      password ? { password } : {}
    )
  );
}

export async function setApiSecret(externalApplicationId, secret) {
  return unwrap(
    await axios.put(
      `${admin}/external-applications/${externalApplicationId}/api-secret`,
      secret ? { secret } : {}
    )
  );
}

export async function fetchWallet(walletId) {
  return unwrap(await axios.get(`${admin}/wallets/${walletId}`));
}

export async function updateCreditLimit(walletId, creditLimit) {
  return unwrap(
    await axios.put(`${admin}/wallets/${walletId}/credit-limit`, {
      credit_limit: creditLimit,
    })
  );
}

export async function settleCredit(walletId, amount, reference, settledByUserId) {
  const body = { amount, reference: reference || undefined };
  if (settledByUserId) {
    body.settled_by_user_id = settledByUserId;
  }
  return unwrap(await axios.post(`${admin}/wallets/${walletId}/settlements`, body));
}

export async function fetchUrlPermissions(externalApplicationId) {
  return (
    unwrap(
      await axios.get(
        `${admin}/external-applications/${externalApplicationId}/url-permissions`
      )
    ) ?? []
  );
}

export async function upsertUrlPermission(externalApplicationId, endpoint, allowed) {
  return unwrap(
    await axios.put(
      `${admin}/external-applications/${externalApplicationId}/url-permissions`,
      { endpoint, allowed }
    )
  );
}

export async function deleteUrlPermission(externalApplicationId, endpoint) {
  await axios.delete(
    `${admin}/external-applications/${externalApplicationId}/url-permissions/${encodeURIComponent(endpoint)}`
  );
}

export async function fetchUrlLimits(externalApplicationId) {
  return (
    unwrap(
      await axios.get(
        `${admin}/external-applications/${externalApplicationId}/url-limits`
      )
    ) ?? []
  );
}

export async function upsertUrlLimit(externalApplicationId, payload) {
  return unwrap(
    await axios.put(
      `${admin}/external-applications/${externalApplicationId}/url-limits`,
      payload
    )
  );
}

export async function deleteUrlLimit(externalApplicationId, endpoint) {
  await axios.delete(
    `${admin}/external-applications/${externalApplicationId}/url-limits/${encodeURIComponent(endpoint)}`
  );
}

export async function fetchAllowedIps(externalApplicationId) {
  return (
    unwrap(
      await axios.get(
        `${admin}/external-applications/${externalApplicationId}/allowed-ips`
      )
    ) ?? []
  );
}

export async function addAllowedIp(externalApplicationId, ipAddress, label) {
  return unwrap(
    await axios.post(
      `${admin}/external-applications/${externalApplicationId}/allowed-ips`,
      { ip_address: ipAddress, label: label || undefined }
    )
  );
}

export async function deleteAllowedIp(externalApplicationId, id) {
  await axios.delete(
    `${admin}/external-applications/${externalApplicationId}/allowed-ips/${id}`
  );
}

export async function fetchApplicationContacts(externalApplicationId) {
  return (
    unwrap(
      await axios.get(
        `${admin}/external-applications/${externalApplicationId}/contacts`
      )
    ) ?? []
  );
}

export async function createApplicationContact(externalApplicationId, payload) {
  return unwrap(
    await axios.post(
      `${admin}/external-applications/${externalApplicationId}/contacts`,
      payload
    )
  );
}

export async function updateApplicationContact(
  externalApplicationId,
  contactId,
  payload
) {
  return unwrap(
    await axios.put(
      `${admin}/external-applications/${externalApplicationId}/contacts/${contactId}`,
      payload
    )
  );
}

export async function deleteApplicationContact(externalApplicationId, contactId) {
  await axios.delete(
    `${admin}/external-applications/${externalApplicationId}/contacts/${contactId}`
  );
}

export async function fetchVirtualAccounts(externalApplicationId) {
  return (
    unwrap(
      await axios.get(
        `${admin}/external-applications/${externalApplicationId}/virtual-accounts`
      )
    ) ?? []
  );
}

export async function createVirtualAccount(externalApplicationId, ifscCode) {
  return unwrap(
    await axios.post(
      `${admin}/external-applications/${externalApplicationId}/virtual-accounts`,
      ifscCode ? { ifsc_code: ifscCode } : {}
    )
  );
}

export async function closeVirtualAccount(externalApplicationId, id) {
  await axios.delete(
    `${admin}/external-applications/${externalApplicationId}/virtual-accounts/${id}`
  );
}

export async function fetchLedger(walletId, page = 1, perPage = 20, filters = {}) {
  const params = {
    page,
    per_page: perPage,
    ...compactFilters(filters),
  };
  return unwrap(await axios.get(`${wallets}/${walletId}/ledger`, { params }));
}

export async function fetchTransactions(walletId, page = 1, perPage = 20, filters = {}) {
  const params = {
    page,
    per_page: perPage,
    ...compactFilters(filters),
  };
  return unwrap(
    await axios.get(`${wallets}/${walletId}/transactions`, { params })
  );
}

function compactFilters(filters = {}) {
  const out = {};
  for (const key of ["from", "to", "type", "status"]) {
    const value = filters[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      out[key] = String(value).trim();
    }
  }
  return out;
}

export async function fetchTransaction(walletId, transactionId) {
  return unwrap(
    await axios.get(`${wallets}/${walletId}/transactions/${transactionId}`)
  );
}

export async function manualCredit(walletId, amount, notes) {
  return unwrap(
    await axios.post(`${wallets}/${walletId}/credit`, {
      amount,
      type: "manual_credit",
      notes: notes || undefined,
    })
  );
}

export async function manualDebit(walletId, amount, notes) {
  return unwrap(
    await axios.post(`${wallets}/${walletId}/debit`, {
      amount,
      type: "manual_debit",
      notes: notes || undefined,
    })
  );
}

export async function fetchWalletNotifications(page = 1, perPage = 20, externalApplicationId) {
  const params = { page, per_page: perPage };
  if (externalApplicationId) {
    params.external_application_id = externalApplicationId;
  }
  return unwrap(await axios.get(`${admin}/wallet-notifications`, { params }));
}

export async function fetchWhiteLabel(clientId) {
  const params = {};
  if (clientId) {
    params.client_id = clientId;
  }
  return unwrap(await axios.get(`${admin}/white-label`, { params }));
}

export async function saveWhiteLabel(payload) {
  return unwrap(await axios.put(`${admin}/white-label`, payload));
}

export async function refundTransaction(
  walletId,
  referenceTransactionId,
  amount,
  notes
) {
  const body = {
    reference_transaction_id: referenceTransactionId,
    notes: notes || undefined,
  };
  if (amount !== undefined && amount !== null && String(amount).trim() !== "") {
    body.amount = amount;
  }
  return unwrap(await axios.post(`${wallets}/${walletId}/refund`, body));
}
